import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { LoginResult } from '@mealdesk/shared';
import AuthShell, { fieldClass } from '../components/AuthShell.js';
import { Button } from '../components/ui.js';
import { useT } from '../lib/i18n.js';
import { apiFetch } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

type Session = Omit<LoginResult, 'refreshToken'> & { mustChangePassword?: boolean };

/**
 * Email and password.
 *
 * It used to mail a one-time link on every sign-in, which meant opening a
 * mailbox before lunch — on a phone that often has no work mail on it at all.
 * The link survives for the two moments it earns the trouble: proving the
 * address the first time, and getting back in after forgetting. The session
 * then lasts ninety days on a sliding window (§4.6), so neither is frequent.
 */
export default function SignInPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const { t } = useT();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const session = await apiFetch<Session>('/api/auth/login', {
        method: 'POST',
        body: { email, password },
        anonymous: true,
      });
      setSession(session);
      // Still on the password somebody handed over: nothing else will work.
      navigate(session.mustChangePassword ? '/change-password' : '/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? t(err.message) : t('Could not sign you in'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title={t('Sign in')}
      subtitle={t('Your work email and the password you chose. You stay signed in for 90 days.')}
      footer={
        <Link to="/password" className="font-extrabold text-brand-ink">
          {t('First time here, or forgot your password?')}
        </Link>
      }
    >
      <form
        className="flex flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label
          htmlFor="email"
          className="mb-2 block text-[13px] font-extrabold text-muted md:text-[14.5px]"
        >
          {t('Work email')}
        </label>
        <input
          id="email"
          type="email"
          inputMode="email"
          autoComplete="username"
          placeholder="you@kokomo-plant.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={fieldClass}
        />

        <label
          htmlFor="password"
          className="mb-2 block text-[13px] font-extrabold text-muted md:text-[14.5px]"
        >
          {t('Password')}
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={fieldClass}
        />

        <Button type="submit" disabled={busy || !email.includes('@') || password.length === 0}>
          {busy ? `${t('Sign in')}…` : t('Sign in')}
        </Button>

        {error && (
          <p className="mt-3.5 rounded-tile bg-danger-soft px-4 py-3.5 text-[13.5px] font-bold text-danger md:text-[15px]">
            {error}
          </p>
        )}

        <div className="mt-4 flex items-start gap-2.5 rounded-tile bg-white px-4 py-3.5">
          <span aria-hidden className="text-[14px] md:text-[15px]">
            🔒
          </span>
          <span className="text-[13.5px] leading-[1.5] text-muted md:text-[15px]">
            {t('Only addresses your company registered can sign in. Guests get a temporary pass for their visit dates.')}
          </span>
        </div>
      </form>
    </AuthShell>
  );
}
